import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ConstructionLayout from './ConstructionLayout';
import { useTheme } from '../../contexts/ThemeContext';
import { useModule } from '../../contexts/ModuleContext';
import { fetchProjects, updateProject } from '../../services/projectService';
import { uploadOrthoJob } from '../../services/orthoService';
import { Save, FolderPlus, Building2, CheckCircle2, AlertCircle, Upload, Image as ImageIcon } from 'lucide-react';
import LoadingSpinner from '../shared/LoadingSpinner';
import { CATEGORIES, STATUS_OPTIONS } from './AddProjectModal';
import { toast } from 'react-toastify';
import { hasScope, SCOPES } from '../../utils/authStorage';

export default function ProjectSettingsPage() {
  const { isDarkMode } = useTheme();
  const canWrite = hasScope(SCOPES.CONSTRUCTION_WRITE);
  const navigate = useNavigate();
  const { activeProject, setActiveProject } = useModule();

  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  // Upload Data (aerial orthomosaic)
  const [uploadProjectId, setUploadProjectId] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCropPx, setUploadCropPx] = useState(2048);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    category: '',
    city: '',
    state: '',
    status: 'Active',
  });

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchProjects();
      setProjects(data || []);
      if (data && data.length > 0) {
        // Default to activeProject if available, else first project
        const match = data.find((p) => String(p.numericId || p.id) === String(activeProject?.numericId || activeProject?.id));
        const initial = match || data[0];
        setSelectedProjectId(initial.numericId || initial.id);
        setFormData({
          name: initial.name || '',
          displayName: initial.displayName || initial.name || '',
          category: initial.category || '',
          city: initial.city || '',
          state: initial.state || '',
          status: initial.status || 'Active',
        });
        setStatusMessage(null);
      }
    } catch (err) {
      console.error('Failed to load projects for settings:', err);
      toast.error('Failed to load projects.');
    } finally {
      setLoading(false);
    }
  }, [activeProject?.id, activeProject?.numericId]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const populateForm = (proj) => {
    if (!proj) return;
    setFormData({
      name: proj.name || '',
      displayName: proj.displayName || proj.name || '',
      category: proj.category || '',
      city: proj.city || '',
      state: proj.state || '',
      status: proj.status || 'Active',
    });
    setStatusMessage(null);
  };

  const handleSelectProject = (e) => {
    const id = e.target.value;
    setSelectedProjectId(id);
    const chosen = projects.find((p) => String(p.numericId || p.id) === String(id));
    if (chosen) {
      populateForm(chosen);
      setActiveProject({
        id: chosen.id,
        numericId: chosen.numericId,
        name: chosen.name,
        location: chosen.location,
        lastSurvey: chosen.lastSurvey,
        imageUrl: chosen.imageUrl,
      });
    }
  };

  const handleInputChange = (field, val) => {
    setFormData((prev) => ({ ...prev, [field]: val }));
    setStatusMessage(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedProjectId || !canWrite) return;

    setIsSaving(true);
    setStatusMessage(null);

    const chosen = projects.find((p) => String(p.numericId || p.id) === String(selectedProjectId));
    const targetNumericId = chosen?.numericId || selectedProjectId;

    try {
      const payload = {
        name: formData.name,
        displayName: formData.displayName,
        category: formData.category,
        city: formData.city,
        state: formData.state,
        status: formData.status,
      };

      await updateProject(targetNumericId, payload);

      toast.success('Project configuration saved successfully!');
      setStatusMessage({ type: 'success', text: 'Configuration saved successfully.' });

      // Update local state list
      const updatedList = projects.map((p) => {
        if (String(p.numericId || p.id) === String(selectedProjectId)) {
          const loc = formData.city && formData.state ? `${formData.city}, ${formData.state}` : (formData.city || formData.state || p.location);
          return {
            ...p,
            name: formData.name,
            displayName: formData.displayName,
            category: formData.category,
            city: formData.city,
            state: formData.state,
            status: formData.status,
            location: loc,
          };
        }
        return p;
      });

      setProjects(updatedList);

      // Update activeProject in ModuleContext
      if (chosen) {
        setActiveProject({
          ...chosen,
          name: formData.name,
          location: formData.city && formData.state ? `${formData.city}, ${formData.state}` : chosen.location,
        });
      }
    } catch (err) {
      console.error('Save project error:', err);
      const msg = err.message || 'Failed to save project settings';
      toast.error(msg);
      setStatusMessage({ type: 'error', text: msg });
    } finally {
      setIsSaving(false);
    }
  };

  const selectedProject = projects.find((p) => String(p.numericId || p.id) === String(selectedProjectId));

  const handleUploadData = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      setUploadMsg({ type: 'error', text: 'Please choose an image file (GeoTIFF / JPG / PNG).' });
      return;
    }
    if (!uploadProjectId) {
      setUploadMsg({ type: 'error', text: 'Please select a project.' });
      return;
    }
    setUploading(true);
    setUploadMsg(null);
    try {
      const job = await uploadOrthoJob(uploadFile, {
        projectId: uploadProjectId,
        cropPx: Number(uploadCropPx) || 2048,
        name: uploadFile.name,
      });
      setUploadMsg({ type: 'success', text: `Uploaded. Slicing started (job ${String(job.id).slice(0, 8)}…). View it under Aerial Data.` });
      setUploadFile(null);
      const fileInput = document.getElementById('ortho-upload-input');
      if (fileInput) fileInput.value = '';
    } catch (err) {
      setUploadMsg({ type: 'error', text: err.message || 'Upload failed' });
    } finally {
      setUploading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '9px 13px',
    borderRadius: '8px',
    border: `1px solid ${isDarkMode ? '#272c30' : '#d1d5db'}`,
    backgroundColor: isDarkMode ? '#0f141b' : '#ffffff',
    color: isDarkMode ? '#ffffff' : '#111827',
    fontSize: '13.5px',
    boxSizing: 'border-box',
    outline: 'none',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '12.5px',
    fontWeight: 600,
    color: isDarkMode ? '#e8eaed' : '#374151',
    marginBottom: '6px',
  };

  return (
    <ConstructionLayout title="Project Settings" showProjectSelector={false}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '720px' }}>
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: isDarkMode ? '#F3F4F6' : '#111827', margin: 0 }}>
            Project Settings
          </h2>
          <p style={{ fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280', margin: '4px 0 0 0' }}>
            Configure and manage parameters for created construction projects.
          </p>
        </div>

        {loading ? (
          <div style={{ padding: '48px 0', display: 'flex', justifyContent: 'center' }}>
            <LoadingSpinner message="Loading projects..." />
          </div>
        ) : projects.length === 0 ? (
          /* Empty state: No projects created yet */
          <div
            style={{
              backgroundColor: isDarkMode ? '#111622' : '#FFFFFF',
              border: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`,
              borderRadius: '12px',
              padding: '48px 24px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                backgroundColor: isDarkMode ? '#1E2330' : '#F3F4F6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: isDarkMode ? '#9CA3AF' : '#6B7280',
              }}
            >
              <Building2 size={28} />
            </div>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, color: isDarkMode ? '#F3F4F6' : '#111827', margin: 0 }}>
                No Projects Found
              </h3>
              <p style={{ fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280', margin: '6px 0 0 0', maxWidth: '360px' }}>
                There are no construction projects created yet. Create a project first to configure its parameters.
              </p>
            </div>
            <button
              onClick={() => navigate('/construction/projects')}
              style={{
                backgroundColor: '#0082F8',
                border: 'none',
                color: '#FFF',
                padding: '9px 18px',
                borderRadius: '8px',
                fontSize: '13.5px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <FolderPlus size={16} /> Go to Projects
            </button>
          </div>
        ) : (
          /* Real Project Configuration Form */
          <form
            onSubmit={handleSave}
            style={{
              backgroundColor: isDarkMode ? '#111622' : '#FFFFFF',
              border: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`,
              borderRadius: '12px',
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '18px',
            }}
          >
            {/* Project Picker */}
            <div>
              <label style={labelStyle}>Select Project to Configure *</label>
              <select
                value={selectedProjectId || ''}
                onChange={handleSelectProject}
                style={{
                  ...inputStyle,
                  cursor: 'pointer',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  paddingRight: '36px',
                  backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='${isDarkMode ? '%239aa0aa' : '%236b7280'}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center',
                  backgroundSize: '14px',
                }}
              >
                {projects.map((p) => (
                  <option
                    key={p.numericId || p.id}
                    value={p.numericId || p.id}
                    style={{ backgroundColor: isDarkMode ? '#12171e' : '#ffffff', color: isDarkMode ? '#e8eaed' : '#111827' }}
                  >
                    {p.displayName || p.name} ({p.code || p.id})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ height: '1px', backgroundColor: isDarkMode ? '#1E2433' : '#E5E7EB', margin: '4px 0' }} />

            {/* Read-only Project Code */}
            {selectedProject?.code && (
              <div>
                <label style={labelStyle}>
                  Project Code <span style={{ fontSize: '11px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>(Unique Identifier)</span>
                </label>
                <input
                  type="text"
                  value={selectedProject.code}
                  disabled
                  style={{
                    ...inputStyle,
                    backgroundColor: isDarkMode ? '#1a1f28' : '#f1f3f5',
                    color: isDarkMode ? '#9aa0aa' : '#6b7280',
                    cursor: 'not-allowed',
                  }}
                />
              </div>
            )}

            {/* Project Name */}
            <div>
              <label style={labelStyle}>Project Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
                placeholder="e.g. Embassy Business Park"
                style={inputStyle}
              />
            </div>

            {/* Display Name */}
            <div>
              <label style={labelStyle}>Display Name</label>
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => handleInputChange('displayName', e.target.value)}
                placeholder="e.g. Embassy Eden – Phase 1"
                style={inputStyle}
              />
            </div>

            {/* Category */}
            <div>
              <label style={labelStyle}>Category</label>
              <select
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                style={{
                  ...inputStyle,
                  cursor: 'pointer',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  paddingRight: '36px',
                  backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='${isDarkMode ? '%239aa0aa' : '%236b7280'}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center',
                  backgroundSize: '14px',
                }}
              >
                <option value="" style={{ backgroundColor: isDarkMode ? '#12171e' : '#ffffff', color: isDarkMode ? '#e8eaed' : '#111827' }}>-- Select Category --</option>
                {[...CATEGORIES, ...(formData.category && !CATEGORIES.includes(formData.category) ? [formData.category] : [])].map((cat) => (
                  <option key={cat} value={cat} style={{ backgroundColor: isDarkMode ? '#12171e' : '#ffffff', color: isDarkMode ? '#e8eaed' : '#111827' }}>{cat}</option>
                ))}
              </select>
            </div>

            {/* City & State */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div>
                <label style={labelStyle}>City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  placeholder="e.g. Bengaluru"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>State</label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  placeholder="e.g. Karnataka"
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Project Status */}
            <div>
              <label style={labelStyle}>Project Status</label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                style={{
                  ...inputStyle,
                  cursor: 'pointer',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  paddingRight: '36px',
                  backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='${isDarkMode ? '%239aa0aa' : '%236b7280'}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center',
                  backgroundSize: '14px',
                }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value} style={{ backgroundColor: isDarkMode ? '#12171e' : '#ffffff', color: isDarkMode ? '#e8eaed' : '#111827' }}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Feedback message if any */}
            {statusMessage && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                  padding: '10px 14px',
                  borderRadius: '6px',
                  backgroundColor: statusMessage.type === 'success' ? (isDarkMode ? 'rgba(0,175,100,0.15)' : '#ecfdf5') : (isDarkMode ? 'rgba(239,68,68,0.15)' : '#fef2f2'),
                  color: statusMessage.type === 'success' ? '#00af64' : '#ef4444',
                  border: `1px solid ${statusMessage.type === 'success' ? (isDarkMode ? 'rgba(0,175,100,0.3)' : '#a7f3d0') : (isDarkMode ? 'rgba(239,68,68,0.3)' : '#fecaca')}`,
                }}
              >
                {statusMessage.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                <span>{statusMessage.text}</span>
              </div>
            )}

            {/* Actions */}
            {!canWrite ? (
              <p style={{ fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280', margin: '10px 0 0 0', textAlign: 'right' }}>
                Read-only — only tenant admins can change project settings.
              </p>
            ) : (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
              <button
                type="submit"
                disabled={isSaving}
                style={{
                  backgroundColor: '#0082F8',
                  border: 'none',
                  color: '#FFF',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '13.5px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  opacity: isSaving ? 0.7 : 1,
                }}
              >
                <Save size={15} />
                {isSaving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
            )}
          </form>
        )}

        {/* Upload Data — project-specific large image (orthomosaic) */}
        {projects.length > 0 && (
          <form
            onSubmit={handleUploadData}
            style={{
              backgroundColor: isDarkMode ? '#111622' : '#FFFFFF',
              border: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`,
              borderRadius: '12px',
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '18px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ImageIcon size={18} color="#0082F8" />
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: isDarkMode ? '#F3F4F6' : '#111827', margin: 0 }}>
                Upload Data
              </h3>
            </div>
            <p style={{ fontSize: '12.5px', color: isDarkMode ? '#9CA3AF' : '#6B7280', margin: 0 }}>
              Upload a large orthomosaic (GeoTIFF / JPG / PNG). It is sliced with 15% overlap; crops are stored for review under Aerial Data.
            </p>

            <div>
              <label style={labelStyle}>Select Project *</label>
              <select
                value={uploadProjectId}
                onChange={(e) => setUploadProjectId(e.target.value)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="">— Select Project —</option>
                {projects.map((p) => (
                  <option key={p.numericId || p.id} value={p.numericId || p.id}>
                    {p.displayName || p.name} ({p.code || p.id})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Image File (GeoTIFF / JPG / JPEG / PNG) *</label>
              <input
                id="ortho-upload-input"
                type="file"
                accept=".tif,.tiff,.geotiff,.jpg,.jpeg,.png"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                style={{ ...inputStyle, padding: '8px' }}
              />
            </div>

            <div>
              <label style={labelStyle}>Crop Pixel Size</label>
              <input
                type="number"
                min={256}
                max={8192}
                step={32}
                value={uploadCropPx}
                onChange={(e) => setUploadCropPx(e.target.value)}
                style={inputStyle}
              />
              <span style={{ fontSize: '11px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Default 2048. Overlap is fixed at 15%.</span>
            </div>

            {uploadMsg && (
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', borderRadius: '8px', fontSize: '12.5px',
                  backgroundColor: uploadMsg.type === 'success' ? (isDarkMode ? 'rgba(0,175,100,0.15)' : '#ecfdf5') : (isDarkMode ? 'rgba(239,68,68,0.15)' : '#fef2f2'),
                  color: uploadMsg.type === 'success' ? '#00af64' : '#ef4444',
                }}
              >
                {uploadMsg.type === 'success' ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />}
                <span>{uploadMsg.text}</span>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="submit"
                disabled={uploading}
                style={{
                  backgroundColor: '#0082F8', border: 'none', color: '#FFF', padding: '10px 20px',
                  borderRadius: '8px', fontSize: '13.5px', fontWeight: 600, display: 'flex',
                  alignItems: 'center', gap: '8px', cursor: uploading ? 'not-allowed' : 'pointer',
                  opacity: uploading ? 0.7 : 1,
                }}
              >
                <Upload size={15} />
                {uploading ? 'Uploading…' : 'Upload Data'}
              </button>
            </div>
          </form>
        )}
      </div>
    </ConstructionLayout>
  );
}
